<template>
  <Teleport to="body">
    <Transition name="fade">
      <div v-if="visible" class="update-overlay">
        <div class="update-modal">
          <!-- 图标 -->
          <div class="icon-container">
            <svg
              v-if="!isUpdating"
              class="icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            <div v-else class="spinner"></div>
          </div>

          <!-- 标题 -->
          <h2 class="title">
            {{ isUpdating ? t('update.updating') : t('update.available') }}
          </h2>

          <!-- 版本信息 -->
          <div v-if="updateInfo && !isUpdating" class="version-info">
            <span class="version">v{{ updateInfo.version }}</span>
          </div>

          <!-- 更新说明 -->
          <p v-if="updateInfo?.notes && !isUpdating" class="notes">
            {{ updateInfo.notes }}
          </p>

          <!-- 进度条 -->
          <div v-if="isUpdating" class="progress-container">
            <div class="progress-bar">
              <div class="progress-fill"></div>
            </div>
            <p class="progress-text">{{ t('update.downloading') }}</p>
          </div>

          <!-- 按钮组 -->
          <div v-if="!isUpdating" class="button-group">
            <button class="btn-later" @click="$emit('close')">
              {{ t('update.later') }}
            </button>
            <button class="btn-update" @click="$emit('update')">
              {{ t('update.now') }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import type { UpdateInfo } from '@/composables/useUpdater'

const { t } = useI18n()

defineProps<{
  visible: boolean
  updateInfo: UpdateInfo | null
  isUpdating: boolean
}>()

defineEmits<{
  close: []
  update: []
}>()
</script>

<style scoped>
.update-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.update-modal {
  width: 420px;
  background: linear-gradient(135deg, #1a1a2e, #16213e);
  border-radius: 20px;
  padding: 40px;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
}

.icon-container {
  margin-bottom: 20px;
}

.icon {
  width: 60px;
  height: 60px;
  color: #22c55e;
  animation: bounce 2s ease-in-out infinite;
}

.spinner {
  width: 50px;
  height: 50px;
  border: 4px solid rgba(255, 255, 255, 0.1);
  border-top-color: #3b82f6;
  border-radius: 50%;
  margin: 0 auto;
  animation: spin 1s linear infinite;
}

.title {
  font-size: 22px;
  font-weight: bold;
  color: white;
  margin-bottom: 16px;
}

.version-info {
  margin-bottom: 16px;
}

.version {
  display: inline-block;
  padding: 6px 16px;
  background: rgba(34, 197, 94, 0.2);
  border: 1px solid rgba(34, 197, 94, 0.5);
  border-radius: 20px;
  color: #22c55e;
  font-weight: 600;
}

.notes {
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 24px;
  max-height: 100px;
  overflow-y: auto;
}

.progress-container {
  margin: 24px 0;
}

.progress-bar {
  height: 6px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  width: 0;
  background: linear-gradient(90deg, #3b82f6, #8b5cf6);
  border-radius: 3px;
  animation: progress 3s ease-in-out infinite;
}

.progress-text {
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
  margin-top: 12px;
}

.button-group {
  display: flex;
  gap: 16px;
  margin-top: 24px;
}

.btn-later,
.btn-update {
  flex: 1;
  height: 46px;
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-later {
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.btn-later:hover {
  background: rgba(255, 255, 255, 0.2);
}

.btn-update {
  background: linear-gradient(135deg, #22c55e, #16a34a);
  color: white;
}

.btn-update:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 20px rgba(34, 197, 94, 0.4);
}

@keyframes bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@keyframes progress {
  0% { width: 0; }
  50% { width: 70%; }
  100% { width: 100%; }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
